import { ApiOutlined, CodeOutlined, CopyOutlined, DatabaseOutlined } from "@ant-design/icons";
import { Button, Descriptions, Empty, Spin, Tag, message } from "antd";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import InsideHeader from "../../components/InsideHeader";
import { fetchSandbox } from "../../features/sandbox/sandboxSlice";
import { dateForHumans } from "../../utils";
import "./sandbox.css";

function SandBox() {
  const dispatch = useDispatch();
  const { sandData, loading } = useSelector((state) => state.sand);

  const sandbox = sandData && typeof sandData === "object" && !Array.isArray(sandData) ? sandData : {};
  const hasSandboxData = Object.keys(sandbox).length > 0;
  const statusLabel = sandbox?.active ? "Active" : "Inactive";

  useEffect(() => {
    dispatch(fetchSandbox());
  }, [dispatch]);

  const handleCopyToClipboard = (text) => {
    if (!text) {
      message.warning("Nothing to copy yet");
      return;
    }

    navigator?.clipboard
      ?.writeText(String(text))
      ?.then(() => {
        message.success("Copied to clipboard");
      })
      .catch(() => {
        message.error("Failed to copy");
      });
  };

  const renderCopyValue = (value) => (
    <div className="sandbox-copy-row">
      <span className="sandbox-copy-text">{value || "N/A"}</span>
      {value ? (
        <Button
          type="text"
          icon={<CopyOutlined />}
          onClick={() => handleCopyToClipboard(value)}
          className="sandbox-copy-button"
        >
          Copy
        </Button>
      ) : null}
    </div>
  );

  const infoItems = [
    {
      key: "status",
      label: "Status",
      children: (
        <Tag color={sandbox?.active ? "green" : "default"} className="sandbox-status-tag">
          {statusLabel}
        </Tag>
      ),
    },
    {
      key: "apiKey",
      label: "API key",
      children: renderCopyValue(sandbox?.apiKey),
    },
    {
      key: "clientName",
      label: "Client name",
      children: sandbox?.clientName || "N/A",
    },
    {
      key: "createdDate",
      label: "Created date",
      children: sandbox?.createdDate ? dateForHumans(sandbox?.createdDate) : "N/A",
    },
    {
      key: "expiryDate",
      label: "Expiry date",
      children: sandbox?.expirationDate ? dateForHumans(sandbox?.expirationDate) : "N/A",
    },
    {
      key: "endpoint",
      label: "Endpoint",
      children: renderCopyValue(sandbox?.apiEndPoint),
    },
  ];

  const renderCodeBlock = (value, emptyLabel) => (
    <div className="sandbox-code-card">
      <pre className="sandbox-code-block">{value || emptyLabel}</pre>
      {value ? (
        <Button
          type="default"
          icon={<CopyOutlined />}
          onClick={() => handleCopyToClipboard(value)}
          className="sandbox-code-copy"
        >
          Copy
        </Button>
      ) : null}
    </div>
  );

  const formattedResponse = (() => {
    try {
      return sandbox?.apiResponse ? JSON.stringify(JSON.parse(sandbox.apiResponse), null, 2) : "";
    } catch (error) {
      return sandbox?.apiResponse || "";
    }
  })();

  return (
    <div className="sandbox-page">
      <InsideHeader
        title="Developer Sandbox"
        subtitle="Review API credentials, sample requests, and a response payload for quick integration testing."
        back
        compact
      />

      <div className="sandbox-shell">
        <section className="sandbox-summary-grid">
          <article className="sandbox-summary-card">
            <span>Status</span>
            <strong>{statusLabel}</strong>
            <p>Current sandbox availability for this account.</p>
          </article>

          <article className="sandbox-summary-card">
            <span>Client</span>
            <strong>{sandbox?.clientName || "N/A"}</strong>
            <p>Workspace name attached to the sandbox credentials.</p>
          </article>

          <article className="sandbox-summary-card">
            <span>Endpoint</span>
            <strong>{sandbox?.apiEndPoint ? "Configured" : "Pending"}</strong>
            <p>Use the endpoint card below when you need the exact URL.</p>
          </article>
        </section>

        {loading ? (
          <div className="sandbox-loading">
            <Spin />
            <span>Loading sandbox details...</span>
          </div>
        ) : (
          <div className="sandbox-grid">
            <section className="sandbox-card sandbox-info-card">
              <div className="sandbox-card-header">
                <div className="sandbox-card-icon">
                  <ApiOutlined />
                </div>
                <div>
                  <h2>API information</h2>
                  <p>Credentials and connection details for the sandbox environment.</p>
                </div>
              </div>

              {hasSandboxData ? (
                <Descriptions column={1} colon={false} items={infoItems} className="sandbox-descriptions" />
              ) : (
                <Empty description="Sandbox details are not available yet." />
              )}
            </section>

            <section className="sandbox-card">
              <div className="sandbox-card-header">
                <div className="sandbox-card-icon">
                  <CodeOutlined />
                </div>
                <div>
                  <h2>cURL samples</h2>
                  <p>Copy a request payload and start testing right away.</p>
                </div>
              </div>

              <div className="sandbox-code-grid">
                <div>
                  <div className="sandbox-code-title">Single request</div>
                  {renderCodeBlock(sandbox?.apiPayload, "No single-request sample available.")}
                </div>

                <div>
                  <div className="sandbox-code-title">Multiple request</div>
                  {renderCodeBlock(sandbox?.apiPayloadMultiple, "No multiple-request sample available.")}
                </div>
              </div>
            </section>

            <section className="sandbox-card">
              <div className="sandbox-card-header">
                <div className="sandbox-card-icon">
                  <DatabaseOutlined />
                </div>
                <div>
                  <h2>Response example</h2>
                  <p>Reference response payload to help downstream integration work.</p>
                </div>
              </div>

              {renderCodeBlock(formattedResponse, "No sample response available.")}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}

export default SandBox;
